import { Divider, Stack, Typography } from '@mui/material';
import { SupportDocumentSubType, TSupportDocument } from './types';
import {
  Alert,
  DocumentUploaderServiceCallsProps,
  InformationDisplay,
  VerificationStatus,
  WholesaleCertificate,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { isSupportDocumentSubType, TStatus, TWholesaleCertificate } from '@verifime/utils';
import React, { HTMLAttributeAnchorTarget, useEffect, useState } from 'react';
import {
  SupportDocumentTypeOptions,
  generateDisplayRowsAndFieldsAndRules,
  getKeyAttributeLabel,
} from './utils';

interface SupportDocumentDetailsViewProps {
  supportDocumentType: SupportDocumentSubType;
  data: TSupportDocument[];
  certificateHolder: {
    holderName: string;
    holderLink: {
      href: string;
      target?: HTMLAttributeAnchorTarget;
    };
  };
  showVerifiedOnly?: boolean;
  UploadView?: React.ComponentType<{
    entityId: string;
    document: TSupportDocument;
    rejected?: boolean;
    onSubmit: (data: TSupportDocument) => void;
    documentUploadServiceCalls: DocumentUploaderServiceCallsProps;
  }>;
  documentUploadServiceCalls?: DocumentUploaderServiceCallsProps;
  entityId?: string;
}

export default function SupportDocumentDetailsView({
  supportDocumentType,
  data,
  certificateHolder,
  UploadView,
  entityId,
  documentUploadServiceCalls,
}: SupportDocumentDetailsViewProps) {
  const [hasMultipleDocuments, setHasMultipleDocuments] = useState(false);
  const [documents, setDocuments] = useState(data);

  useEffect(() => {
    setHasMultipleDocuments(documents.length > 1);
  }, [documents]);

  useEffect(() => {
    setDocuments(data);
  }, [data]);

  const updatedFile = (data: TSupportDocument) => {
    setDocuments((prevDocuments) => {
      return prevDocuments.map((doc) => (doc.id === data.id ? data : doc));
    });
  };

  const detailView = (document: TSupportDocument, displayKeyAttribute: boolean) => {
    const wholesaleCertificate = isSupportDocumentSubType(document, 'WholesaleCertificate')
      ? (document as TWholesaleCertificate)
      : null;
    switch (document.status) {
      case 'InProgress':
        return <Alert severity="info"> We are verifying the document at the moment. </Alert>;
      case 'Draft':
        return <></>;
      case 'Rejected':
        return UploadView && entityId ? (
          <UploadView
            document={document}
            entityId={entityId}
            onSubmit={updatedFile}
            rejected={true}
            documentUploadServiceCalls={documentUploadServiceCalls}
          />
        ) : (
          <Alert severity="error"> We were unable to verify the document provided. </Alert>
        );
      case 'Verified':
        return (
          <Stack gap={tokens.spacingBase}>
            {wholesaleCertificate ? (
              <WholesaleCertificate
                holder={certificateHolder}
                accountantNumber={wholesaleCertificate.accountantNumber}
                dateIssued={wholesaleCertificate.dateIssued}
                dateOfExpiry={wholesaleCertificate.dateOfExpiry}
              />
            ) : (
              // The display for this is really anything goes right now, as it could be any of the support document here.
              // If we really want to be specific, like wholesale certificate we should be creating a new component
              // for each of the support document type.
              // For now, we will just use the generic information display component to show the details.
              <InformationDisplay<any>
                informationDisplaysRows={
                  generateDisplayRowsAndFieldsAndRules({
                    subType: document.supportDocumentSubType,
                    displayKeyAttribute: displayKeyAttribute,
                    keyAttribute: document.keyAttributeName,
                  }).displayRows
                }
                data={document}
              />
            )}
            {/* Will support the following for verified document later
          <UploadView document={document} entityId={identity.id} onSubmit={updatedFile} /> */}
          </Stack>
        );
    }
  };

  const documentKeyAttributeName = (document: TSupportDocument): string => {
    const keyAttributeName = document.keyAttributeName as keyof TSupportDocument;
    return keyAttributeName ? (document[keyAttributeName] as string) : undefined;
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Stack gap={tokens.spacing2xs}>
          <Typography variant="h6">
            {SupportDocumentTypeOptions[supportDocumentType].type}
          </Typography>
          <Typography variant="body2">
            {SupportDocumentTypeOptions[supportDocumentType].description}
          </Typography>
        </Stack>
        {!hasMultipleDocuments && <VerificationStatus status={documents[0].status as TStatus} />}
      </Stack>
      {documents.map((document, index) => (
        <Stack key={document.id} gap={tokens.spacingBase}>
          {hasMultipleDocuments && (
            <Stack direction="row" gap={tokens.spacing2xs} justifyContent="space-between">
              <Stack direction="row" gap={tokens.spacing2xs}>
                {documentKeyAttributeName(document) && (
                  <>
                    <Typography color="textSecondary" variant="body2">
                      {getKeyAttributeLabel(supportDocumentType, document.keyAttributeName)}
                    </Typography>
                    <Typography color="textPrimary" variant="body2">
                      {documentKeyAttributeName(document)}
                    </Typography>
                  </>
                )}
              </Stack>
              <Stack alignItems="flex-end">
                <VerificationStatus status={document.status as TStatus} />
              </Stack>
            </Stack>
          )}
          {detailView(document, !hasMultipleDocuments)}
          {index < documents.length - 1 && <Divider />}
        </Stack>
      ))}
    </>
  );
}
